import React, { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
	Behaviours,
	Prompts,
	TargetTabs,
	createProvisioningPageExtensions,
} from '@atlassiansox/cross-flow-extensions';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives/compiled';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { fg } from '@atlassian/jira-feature-gating';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import type { recommendationContext } from '../../common/types.tsx';
import { RecommendationContainer } from '../../common/ui/recommendation-section/recommendation-container/index.tsx';
import { RecommendationHeader } from '../../common/ui/recommendation-section/recommendation-header/index.tsx';
import { RecommendationMenuItem } from '../../common/ui/recommendation-section/recommendation-menu-item/index.tsx';
import { RecommendationModal } from '../../common/ui/recommendation-section/recommendation-modal/index.tsx';
import { useProjectMenuRecommendations } from '../../controllers/project-menu-recommendations/index.tsx';
import { projectsMenuDropdownLoadJpdRecommendation } from '../../experiences.tsx';
import { fetchAppRecommendations } from '../../services/fetch-app-recommendations/index.tsx';
import ProductRoadmapProjectAvatar from './illustrations/product-roadmap-project-avatar.tsx';
import {
	DATA_PRIVACY_LINK,
	JPD_ENTITY_ID,
	JPD_RECOMMENDATION_DISMISS_USER_PROPERTY,
	JPD_RECOMMENDATION_KEY,
	JPD_ROADMAP_KEY,
	getProductTypeCcpReferenceIdMapForEnv,
} from './constants.tsx';
import messages from './messages.tsx';
import { hasJpdLicense } from './utils.tsx';

export const JpdRecommendation = () => {
	const [moreInformationModal, setMoreInformationModal] = useState(false);
	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const [, { setRecommendationDismissal }] = useProjectMenuRecommendations();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const accountId = useAccountId();
	const { formatMessage } = useIntl();

	const crossFlow = useCrossFlow();
	const environment = useEnvironment();
	const jpdCloudCCPProductID = getProductTypeCcpReferenceIdMapForEnv(environment).PRODUCT_DISCOVERY;

	useEffect(() => {
		if (crossFlow.isEnabled) {
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'section',
					actionSubjectId: 'projectsMenu',
				},
				{
					recommendedProductIds: [jpdCloudCCPProductID],
					source: 'projectsMenuSection',
				},
				{
					navigationVersion: 'v3',
				},
			);
		} else {
			fireErrorAnalytics({
				error: new Error(crossFlow.reason),
				meta: {
					id: crossFlow.reason,
					packageName: 'jiraAtlassianNavigationRecommendations',
				},
			});
		}
	}, [createAnalyticsEvent, crossFlow, fireViewedUIEvent, jpdCloudCCPProductID]);

	const openCrossFlow = useCallback(
		async (templateId: string) => {
			const extensions = createProvisioningPageExtensions({
				actions: {
					primary: {
						behaviour: Behaviours.LAND,
						prompt: Prompts.NEXT,
						options: {
							targetTab: TargetTabs.SAME,
							urlParams: { templateId },
						},
					},
				},
			});

			if (crossFlow.isEnabled) {
				crossFlow.apiV1.open({
					journey: Journeys.GET_STARTED,
					targetProduct: Targets.JIRA_PRODUCT_DISCOVERY,
					sourceComponent: 'projectsMenu',
					sourceContext: 'tryThisRecommendation',
					extensions,
					v1: true,
				});
			}
		},
		[crossFlow],
	);

	const onRecommendationClicked = useCallback(() => {
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryThisRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
			},
			{
				navigationVersion: 'v3',
			},
		);
		openCrossFlow(JPD_ROADMAP_KEY);
	}, [fireInteractedUIEvent, jpdCloudCCPProductID, openCrossFlow]);

	const onLearnMoreClicked = useCallback(() => {
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'learnMore',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
			},
			{
				navigationVersion: 'v3',
			},
		);
		setMoreInformationModal(true);
	}, [fireInteractedUIEvent, jpdCloudCCPProductID]);

	const onLearnMoreClosed = useCallback(() => {
		setMoreInformationModal(false);
	}, []);

	const onDismissClicked = useCallback(() => {
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
			},
			{
				navigationVersion: 'v3',
			},
		);
		setRecommendationDismissal();
		if (accountId)
			setUserProperties(accountId, JPD_RECOMMENDATION_DISMISS_USER_PROPERTY, 'true').catch(
				(error) =>
					fireErrorAnalytics({
						error,
						meta: {
							id: 'dismissJpdRecommendation',
							packageName: 'jiraAtlassianNavigationRecommendations',
							teamName: 'navigation',
						},
					}),
			);
	}, [accountId, fireInteractedUIEvent, jpdCloudCCPProductID, setRecommendationDismissal]);

	const onClickPrivacyPolicy = useCallback(() => {
		const clickedLinkBaseEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});

		fireUIAnalytics(clickedLinkBaseEvent, 'adControlsPrivacyPolicy');
	}, [createAnalyticsEvent]);

	return (
		<RecommendationContainer>
			<RecommendationHeader
				title={formatMessage(messages.recommendedText)}
				onDismissClicked={onDismissClicked}
				onLearnMoreClicked={onLearnMoreClicked}
			/>
			<RecommendationMenuItem
				title={formatMessage(messages.projectTitleProductRoadmap)}
				description={formatMessage(messages.projectDescriptionProductRoadmap)}
				onClick={onRecommendationClicked}
				lozenge={
					<Lozenge appearance="new" isBold={false}>
						{formatMessage(messages.lozengeText)}
					</Lozenge>
				}
				iconBefore={<ProductRoadmapProjectAvatar />}
			/>
			<RecommendationModal
				isOpen={moreInformationModal}
				onClose={onLearnMoreClosed}
				title={formatMessage(messages.reasonsModalTitle)}
				body={
					<>
						{formatMessage(messages.reasonsModalExplanation, {
							strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
						})}
						<ul>
							<li>{formatMessage(messages.reasonsModalBulletOneSpork)}</li>
							<li>{formatMessage(messages.reasonsModalBulletTwo)}</li>
						</ul>
						<br />
						<Button
							appearance="link"
							spacing="none"
							href={DATA_PRIVACY_LINK}
							target="_blank"
							onClick={onClickPrivacyPolicy}
						>
							{formatMessage(messages.privacyPolicy)}
						</Button>
					</>
				}
				primaryCta={formatMessage(messages.reasonsModalButtonOk)}
			/>
		</RecommendationContainer>
	);
};

const checkEligibility = async (
	recommendationContext: recommendationContext,
	analyticsEvent: UIAnalyticsEvent,
) => {
	const {
		areProductSuggestionsEnabled,
		licensedProducts,
		canCreateProject,
		accountId,
		cloudId,
		environment,
		isInFedRamp,
	} = recommendationContext;

	if (fg('grw-1467_jpd_reco_eligibility_override_gate')) {
		return { isEligible: true, reason: 'Eligibility override gate is enabled' };
	}

	if (!areProductSuggestionsEnabled) {
		return { isEligible: false, reason: 'Product suggestions are disabled' };
	}

	if (hasJpdLicense(licensedProducts)) {
		return { isEligible: false, reason: 'Instance has JPD license' };
	}

	if (!canCreateProject) return { isEligible: false, reason: 'User cannot create projects' };

	if (isInFedRamp) return { isEligible: false, reason: 'User is in FedRamp' };

	const [dismissed, appRecommendations] = await Promise.all([
		getUserProperty(accountId, JPD_RECOMMENDATION_DISMISS_USER_PROPERTY).catch((error) => {
			// If not found, then the user hasn't dismissed the ad.
			// 404 - user property not found,
			if (error.statusCode === 404) return false;

			const expectedErrors = [
				429, // rate limit exceeded
				401, // unauthorised
				403, // forbidden
				null, // client error
				undefined, // client error
			];
			// 50x - network errors
			// We shouldn't see the recommendation
			if (
				expectedErrors.includes(error.statusCode) ||
				error.statusCode >= 500 ||
				isClientFetchError(error)
			)
				return true;
			throw Error(
				`${error.statusCode}, JPD recommendation dismissal failed. ${error.name}: ${error.message}`,
				error,
			);
		}),
		fetchAppRecommendations(cloudId, environment, analyticsEvent),
	]);

	if (dismissed) {
		return { isEligible: false, reason: 'JPD recommendation dismissed' };
	}

	if (!appRecommendations?.some((recommendation) => recommendation?.entityId === JPD_ENTITY_ID)) {
		return { isEligible: false, reason: 'JPD was not in the recommend apps' };
	}

	return { isEligible: true, reason: 'Pass all checks' };
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default {
	key: JPD_RECOMMENDATION_KEY,
	shouldRender: async (
		recommendationContext: recommendationContext,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		projectsMenuDropdownLoadJpdRecommendation.start();
		const { isEligible, reason } = await checkEligibility(recommendationContext, analyticsEvent);

		if (fg('pm_jpd_reco_operational_event_activated')) {
			fireOperationalAnalytics(
				analyticsEvent,
				'jiraAtlassianNavigation.loadJpdRecommendation succeeded',
				{
					reason,
					isEligible,
				},
			);
		}

		projectsMenuDropdownLoadJpdRecommendation.success();

		return isEligible;
	},
	onLoadError: (error: Error) => {
		projectsMenuDropdownLoadJpdRecommendation.failure();
		fireErrorAnalytics({
			error,
			meta: {
				id: 'loadJpdRecommendation',
				packageName: 'jiraAtlassianNavigationRecommendations',
				teamName: 'navigation',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	},
	Component: JpdRecommendation,
};
